// The Washing Plan (Management › Planning & Delivery › Washing Plan): which boats need a wash, planned onto
// the days of a month and ticked off when done. Four washes, each named in English and in Greek (the
// printed schedule carries both, as not everyone on the floor reads English):
//   production boats (the Overview), once a boat is at 96% or more with its engine installed:
//     1. after the sea trial;
//     2. before delivery, before polishing;
//     3. again before delivery, when the second wash was too long ago;
//   service boats (the Service sheet), once their works are finished and before they go back to the client.
// The plan itself (the day each wash is planned for, when it was done and by whom, a note) is kept in the
// app's own database, one record per boat and wash (`washId`). A wash can also be marked "no washing
// required" (a service boat that came in clean, or one the yard has no time for while new boats come
// first): it leaves the plan and the list of washes to plan, with who said so and when, until undone. Each
// model's washing time (the outside and the interior, `WASH_TIMES`) lets the plan add up a day's washing. The
// worker (routes/washPlan.ts) and the page (pages/WashingPlan.tsx, features/washPlan.ts) share this module.
import { z } from 'zod';
import { modelKey } from './model.js';
export const WASH_KINDS = ['seatrial', 'predelivery', 'rewash', 'service'];
/** each wash's names, in English and Greek: `short` for a chip, `name` in full. */
export const WASH_KIND_INFO = {
    seatrial: {
        n: '1', source: 'prod',
        short: 'After sea trial', name: 'Wash after the sea trial',
        shortEl: 'Μετά τη δοκιμή', nameEl: 'Πλύσιμο μετά τη θαλάσσια δοκιμή',
    },
    predelivery: {
        n: '2', source: 'prod',
        short: 'Pre-delivery', name: 'Pre-delivery wash, before polishing',
        shortEl: 'Πριν την παράδοση', nameEl: 'Πλύσιμο πριν την παράδοση, πριν το γυάλισμα',
    },
    rewash: {
        n: '3', source: 'prod',
        short: 'Re-wash', name: 'Re-wash before delivery',
        shortEl: 'Ξαναπλύσιμο', nameEl: 'Ξαναπλύσιμο πριν την παράδοση',
    },
    service: {
        n: 'S', source: 'service',
        short: 'Service', name: 'Wash after service, before delivery',
        shortEl: 'Σέρβις', nameEl: 'Πλύσιμο μετά το σέρβις, πριν την παράδοση',
    },
};
/** a boat is washed only once it is this far through assembly (and its engine is in). */
export const WASH_MIN_PCT = 96;
/** the second wash is "too long ago" for a delivery after this many days, unless the page says otherwise. */
export const REWASH_AFTER_DAYS = 14;
const SMALL = { outside: [90, 90], interior: [60, 90] };
const MID = { outside: [120, 120], interior: [90, 120] };
const BIG = { outside: [90, 120], interior: [120, 240] };
/** each model's washing time, as the washing team gave them (the 38 has none yet) */
export const WASH_TIMES = {
    GT7: SMALL, GT9: SMALL, GTX: SMALL,
    '40': MID, '47': MID,
    '45': BIG, '50': BIG, XPD: BIG,
};
/** a boat's washing time by its model ("A40", "Alpha 50" and "XPD" alike), null when there's none */
export const washTime = (model) => WASH_TIMES[modelKey(model)] ?? null;
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
/** a wash, outside and interior together */
export const washTotal = (t) => add(t.outside, t.interior);
/** the washing time of several boats (a day's washes): the outside, the interior and both, and how many
 *  boats have no time (a model without one, or no model) and so aren't counted */
export function sumWashTimes(models) {
    let outside = [0, 0], interior = [0, 0], counted = 0;
    for (const m of models) {
        const t = washTime(m);
        if (!t)
            continue;
        outside = add(outside, t.outside);
        interior = add(interior, t.interior);
        counted++;
    }
    return { outside, interior, total: add(outside, interior), counted, unknown: models.length - counted };
}
/** a wash's record id: one per boat and wash. */
export const washId = (source, boatKey, kind) => `${source}:${boatKey.trim()}:${kind}`;
/** a real calendar day, 'YYYY-MM-DD'. */
export function isWashDay(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m)
        return false;
    const y = +m[1], mo = +m[2], d = +m[3];
    const t = new Date(Date.UTC(y, mo - 1, d));
    return y >= 2000 && y <= 2199 && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}
const Day = z.string().refine(isWashDay, { message: 'Use a real date, YYYY-MM-DD' });
/** a change to one wash: plan it on a day (null takes it off the plan), tick it done (on a day, today when
 *  none is given) or not, mark it not needed (or needed after all), and / or its note. The boat's name and
 *  model ride along for the record. Not needed takes the wash off the plan and clears a done tick; planning
 *  a day or ticking it done means it's needed after all. */
export const WashInputSchema = z
    .object({
    source: z.enum(['prod', 'service']),
    boatKey: z.string().trim().min(1).max(40),
    kind: z.enum(WASH_KINDS),
    boatName: z.string().trim().max(160).optional(),
    boatModel: z.string().trim().max(60).optional(),
    plannedOn: Day.nullable().optional(),
    done: z.boolean().optional(),
    doneOn: Day.optional(),
    notNeeded: z.boolean().optional(),
    note: z.string().max(300).transform((s) => s.trim()).optional(),
})
    .strict()
    .refine((v) => WASH_KIND_INFO[v.kind].source === v.source, { message: 'That wash is not for that kind of boat' })
    .refine((v) => v.plannedOn !== undefined || v.done !== undefined || v.notNeeded !== undefined || v.note !== undefined, { message: 'Nothing to change' })
    .refine((v) => !(v.notNeeded && (v.done || (v.plannedOn !== undefined && v.plannedOn !== null))), { message: 'A wash that is not needed is not planned or done' });
//# sourceMappingURL=wash.js.map